<template>
  <div>
    <BackLink class="my-4" :to="{ name: 'documents' }" data-testid="documents-back-link">{{ t('backToOverview') }}</BackLink>

    <h1 v-if="props.booth" class="pb-7">
      {{ t('Booth.headline', { name: props.booth.boothNumber }) }}
      {{ t('Booth.ofPartnerCompany', { name: props.booth.partnerCompany?.name }) }}
    </h1>

    <h1 v-else-if="props.event" class="pb-7">
      {{ eventName }}
    </h1>

    <h1 v-else-if="props.partnerCompany" class="pb-7">
      {{ props.partnerCompany.displayName || props.partnerCompany.name }}
    </h1>

    <div v-if="hasBoothOsc" id="booth-services" class="my-8 text-center" data-testid="documents-booth-services-section">
      <h2 class="mb-5">
        {{ t('Booth.boothServiceOrdersTitle') }}
      </h2>
      <p class="mx-auto max-w-xl txt-body-s-regular">
        {{ t('Booth.boothServiceOrdersDesc') }}
      </p>
      <a
        v-if="props.booth?.oscPortalUrl"
        class="btn-primary-color-s mt-5"
        data-testid="documents-booth-services-cta"
        :href="props.booth.oscPortalUrl"
        target="_blank"
      >
        {{ t('Booth.boothServiceOrdersCTA') }}
      </a>
    </div>

    <div class="mt-16" data-testid="documents-panel-section">
      <h1>{{ t('Booth.DocumentPanelTitle') }}</h1>

      <DocumentsPanel
        v-if="props.booth && props.booth.type"
        :gid="props.booth.id"
        :type="props.booth.type"
        :event-id="props.booth.event.id"
        :show-notification="true"
      />

      <DocumentsPanel
        v-if="props.event && props.event.type"
        :gid="props.event.id"
        :type="props.event.type"
        :event-id="props.event.rootEvent.id"
        :show-notification="true"
      />

      <DocumentsPanel v-if="props.partnerCompany" :gid="props.partnerCompany.id" type="PartnerCompany" :show-notification="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { DocumentsPanel } from '@/components'
import { type BoothQuery, type EventQuery, type PartnerCompanyByIdQuery, BoothTypeEnum } from '@/gql/myomr'
import { getLocalizedAttribute } from '@/helpers/localizedAttribute'

const { t, locale } = useI18n()

type PartnerCompanyNode = Extract<PartnerCompanyByIdQuery['node'], { __typename: 'PartnerCompany' }>

const props = defineProps<{
  booth?: Extract<BoothQuery['node'], { __typename: 'Booth' }>
  event?: EventQuery['eventById']
  partnerCompany?: PartnerCompanyNode
}>()

const hasBoothOsc = computed(() => {
  return (
    props.booth?.oscPortalUrl &&
    [
      BoothTypeEnum.BrandedRoomBooth,
      BoothTypeEnum.CreatorLoungeBooth,
      BoothTypeEnum.MeetingRoomBooth,
      BoothTypeEnum.PremiumBooth,
      BoothTypeEnum.PremiumExpoTurnkeyBooth,
      BoothTypeEnum.PremiumLoungeBooth,
      BoothTypeEnum.PremiumOutdoorBooth,
      BoothTypeEnum.TinySpaceBooth,
    ].includes(props.booth?.type as BoothTypeEnum)
  )
})

const eventName = computed(() =>
  props.event
    ? getLocalizedAttribute({
        de: props.event.nameDe,
        en: props.event.nameEn,
        fallback: props.event.name || '',
        locale: locale.value,
      })
    : '',
)
</script>
